<template>
	<div class="content">
		<Loader :soft="true" v-if="loading" />
		<div class="flex">
			<div class="menu">
        <div class="menu-item" v-on:click="active_tab = 'preshipping';" v-bind:class="{ active: active_tab === 'preshipping' }">
          <div class="icon">
            <SvgIcon name="preshipping" />
          </div>
          Sneak Peek
        </div>
				<div class="menu-item" v-on:click="active_tab = 'rebuild';" v-bind:class="{ active: active_tab === 'rebuild' }">
					<div class="icon">
						<SvgIcon name="rebuild" />
					</div>
					Rebuild
				</div>
				<div class="menu-item" v-on:click="active_tab = 'raw_data';" v-bind:class="{ active: active_tab === 'raw_data' }">
					<div class="icon">
						<SvgIcon name="database" />
					</div>
					Raw data
				</div>
        <div class="menu-item" v-on:click="active_tab = 'step_function';" v-bind:class="{ active: active_tab === 'step_function' }">
          <div class="icon">
            <SvgIcon name="database" />
          </div>
          Step function executions
        </div>
        <div
          class="margin-top">
          <button class="small" v-on:click="load">Re-load</button>
        </div>
			</div>
			<div class="diagnostics-tabs">

        <!-- Sneak Peek -->
        <div class="diagnostics-tab" v-show="active_tab === 'preshipping'">
          <template v-if="data">
            <template v-if="data.preshipping_flow">
              <h2>Hold/pause status</h2>
              <p>
                When a sneak peek is on hold, there will be no communication to it, and it cannot
                complete AI finalization.
              </p>
              <p>
                For more information, read
                <a href="https://app.clickup.com/2409014/v/dc/29ghp-41868/29ghp-67615">the documentation</a>
                on this topic.
              </p>

              <div>
                <strong>On hold (by customer service)</strong>:
                {{ data.preshipping_flow.on_hold ? 'YES' : 'NO' }}
              </div>
              <div>
                <strong>AI finalization paused (by step functions)</strong>:
                {{ data.preshipping_flow.ai_finalization_paused_at ? 'YES' : 'NO' }}
              </div>
              <div>
                <strong>Communication stopped permanently (by step functions)</strong>:
                {{ data.preshipping_flow.stopped_at ? 'YES' : 'NO' }}
              </div>

              <div class="margin-top">
                <button v-on:click="setOnHold(true)" :disabled="saving" v-if="!data.preshipping_flow.on_hold">Set on hold</button>
                <button v-on:click="setOnHold(false)" :disabled="saving" v-if="data.preshipping_flow.on_hold">Release from hold</button>
              </div>
            </template>
            <template v-else>There's no Sneak Peek on this order.</template>
          </template>
        </div>

				<!-- Rebuild -->
				<div class="diagnostics-tab" v-show="active_tab == 'rebuild'">

					<Group title="Rebuild" class="margin-top">
            <p>
              Reconstruct the <i>products</i> table from <i>shop_order_lines</i>.
            </p>
						<button :disabled="saving" v-on:click="rebuild">Rebuild products table</button>
					</Group>

				</div>

        <!-- Step function -->
				<div class="step-function-tab" v-show="active_tab == 'step_function'">
          <Group title="Execute" class="margin-top">
            <button :disabled="isStylingProcessStarted" v-on:click="startStylingProcess">Start styling process</button>
          </Group>
					<Group title="Step function execution log" class="margin-top">
            <table class="basic" v-if="step_function_executions.length">
              <thead>
              <tr>
                <th>Name</th>
                <th>Started</th>
                <th>Ended</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in step_function_executions">
                <td><a :href="item.weblink" target="_blank">
                  {{ item.step_function_name }}
                </a></td>
                <td>{{ item.execution_started_at }}</td>
                <td>{{ item.execution_ended_at }}</td>
              </tr>
              </tbody>
            </table>
					</Group>

				</div>

				<!-- Tables -->
				<div class="diagnostics-tab" v-show="active_tab === 'raw_data'">
					<div v-for="(items, group) in info">
					<Group class="margin-top" :title="group">
						<table class="basic" v-if="items.length">
							<thead>
								<tr>
									<th v-for="(column, cid) in columns[group]">{{ cid }}</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="item in items">
									<td v-for="(column, cid) in columns[group]">{{ item[cid] }}</td>
									<td>
										<button
											v-if="typeof delete_urls[group] !== 'undefined'"
											class="smaller white"
											:disabled="saving"
											v-on:click="deleteGroupedItem(group, item)">Delete</button>
									</td>
								</tr>
							</tbody>
						</table>
						<div v-else>Nothing to show here</div>
					</Group>
					</div>
				</div>

			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import tr from 'vue2-datepicker/locale/es/tr'

export default {
  props: [
    'id',
    'autoload',
    'order'
  ],
  data: () => ({
    data: {},
    show_cancellation: false,
    active_tab: 'preshipping',
    loading: false,
    saving: false,
    isStylingProcessStarted: false,
    info: {},
    step_function_executions: [],
    th_styling_step_function_id: 16, // TH_Styling step function ID
    columns: {
      bank_payments: {
        id: {},
        amount: {},
        transfer_date: {},
        comment: {}
      },
      tracking_flows: {
        id: {},
        not_collected_1: {},
        not_collected_2: {},
        not_returned_1: {},
        returned_to_sender: {},
        flow_completed: {},
        flow: {},
        invoicing_extra_days: {},
        last_checked_at: {}
      },
      stripe_events: {
        id: {},
        amount: {},
        charged_at: {}
      },
      scheduled_payments: {
        id: {},
        amount: {},
        payment_date: {},
        capture_attempted_at: {},
        captured_at: {},
        description: {},
        approved_at: {},
        triggers: {},
        authorized_at: {}
      },
      exchange_requests: {
        id: {},
        product_id: {},
        expired_at: {},
        wp_order_id: {},
        cancelled_by_user_at: {}
      },
      exchange_orders: {
        id: {},
        closed_at: {},
        completed_at: {},
        graceperiod_mail_at: {},
        last_capture_attempt_at: {}
      },
      invoices: {
        id: {},
        sent_at: {},
        charged_amount: {},
        charged_at: {},
        capture_attempted_at: {},
        ready_for_capture: {},
        uniconta_booked_at: {}
      },
      return_order_content: {
        id: {},
        file_name: {},
        timestamp: {},
        eanCode: {},
        articleId: {},
        color: {},
        size: {},
        quantity: {},
        reasonCode: {}
      },
      products: {
        id: {},
        woocommerce_product_id: {},
        status: {},
        return_reason: {},
        price: {},
        sku: {},
        ean_code: {},
        feedback: {},
        wants_to_keep: {}
      },
      delivery_notice_lines: {
        id: {},
        qty: {},
        ean_code: {},
        sku: {},
        color: {},
        size: {},
        uniconta_sync_at: {}
      }
    },
    delete_urls: {
      delivery_notice_lines: {
        url: '/delivery-notice-line/{id}'
      }
    },
    wp_url: process.env.VUE_APP_WP_URL
  }),
  methods: {
    deleteGroupedItem (group, item) {
      var url = this.delete_urls[group].url.replace('{id}', item.id)

      Vue.api.delete(url, {}, {
        before: () => this.saving = true,
        always: () => this.saving = false,
        callback: data => this.load()
      })
    },
    startStylingProcess() {
      if (!confirm(
        'You are about to manually start a new AI styling process. \n' +
        'Click OK to continue, otherwise click Cancel.'
      )) {
        return;
      }
      Vue.api.post('/step-function/execute', {
        step_function_id: this.th_styling_step_function_id,
        model_id: this.getId()
      }, {
        before: () => this.saving = true,
        always: () => this.saving = false,
        callback: data => this.load()
      })
    },
    rebuild () {
      Vue.api.post('/order/' + this.getId() + '/rebuild', {}, {
        before: () => this.saving = true,
        always: () => this.saving = false
      })
    },
    execute (url) {
      Vue.api.post(url, {}, {
        before: () => this.saving = true,
        always: () => this.saving = false
      })
    },
    getId () {
      return this.id
    },
    setOnHold (to) {
      const method = to ? 'post' : 'delete'
      Vue.api[method]('/preshipping/' + this.getId() + '/on-hold', {}, {
        before: () => this.saving = true,
        always: () => this.saving = false,
        callback: () => this.load()
      })
    },
    load () {
      Vue.api.get('/order/' + this.getId() + '/diagnostics', {}, {
        before: () => this.loading = true,
        always: () => this.loading = false,
        callback: data => {
          this.data = data
          this.info = data.info
        }
      })

      Vue.api.get('/order/' + this.getId() + '/step-function/executions', {}, {
        before: () => this.loading = true,
        always: () => this.loading = false,
        callback: data => {
          this.step_function_executions = data.data
          const found = data.data.find(item => item.step_function_id === this.th_styling_step_function_id);
          this.isStylingProcessStarted = !!found;
        }
      })
    }
  },
  created () {
    if (this.autoload) {
      this.load()
    }
  }
}
</script>
